/**
 * Forbrukerrådet - Kommunetesten
 * @link https://gitlab01.copyleft.no/FBR/kommunetesten-new
 * @author Anders Evenrud <anders.evenrud@copyleft.no>
 */

// TODO: Cleanu up

import Vue from 'vue'
import Vuex from 'vuex'
import * as CSV from 'csv-string'
import { zipObject, sortBy, groupBy } from 'lodash-es'
import country from './country.js'

Vue.use(Vuex)

/*
 * Checks if a score is invalid
 */
const invalidScore = score => isNaN(parseFloat(score))

/*
 * Simple string matcher
 */
const matchName = query => iter => iter.name.toLowerCase()
  .startsWith(query)

/*
 * Averages a list of numbers
 */
const average = arr => arr.reduce((total, amount, index, array) => {
  return index === array.length - 1
    ? (total + amount) / array.length
    : total + amount
}, 0)

/*
 * An array containing all score keys in our entry definition
 */
const pointKeys = [
  'sub_0', 'sub_0_1', 'sub_0_2', 'sub_0_3',
  'sub_1', 'sub_1_1', 'sub_1_2', 'sub_1_3',
  'sub_2', 'sub_2_1', 'sub_2_2', 'sub_2_3',
  'sub_3', 'sub_3_1', 'sub_3_2',
  'sub_4', 'sub_4_1',
  'sub_5', 'sub_5_1', 'sub_5_2',
  'sub_6', 'sub_6_1', 'sub_6_2'
]

/*
 * County list
 */
const counties = sortBy(
  country.map(({ id, name, scale, center }) => ({ id, name, scale, center })),
  c => c.name
)

/*
 * Averages all scores in a list of entries
 */
const averagePoints = arr =>
  pointKeys.reduce((carry, name) => ({
    ...carry,
    score: average(arr.map(iter => parseFloat(iter.score))),
    [name]: average(arr.map(iter => parseInt(iter[name], 10)))
  }), {})

/*
 * Craetes an object suitable for table in the UI
 */
const createRowData = rows => Object.values(groupBy(rows, 'kid'))
  .filter(group => group.length > 0)
  .map(group => {
    const c = counties.find(item => item.id === group[0].fid)

    return {
      ...group[0],
      countyName: c ? c.name : 'N/A',
      points: averagePoints(group).score
    }
  })

const countyFilterer = (moves, year) => c => {
  /* eslint-disable camelcase */
  const moved = moves[c.id]
  if (moved) {
    if (moved.changed_year <= year && moved.merged_to_id > 0) {
      return false
    } else if (moved.changed_year >= year && moved.merged_legacy_ids.length > 0) {
      return false
    }
  }

  return true
}

export default new Vuex.Store({
  state: {
    years: [],
    data: [],
    maps: {},
    moves: [],
    countyMoveMap: {}
  },

  mutations: {
    LOAD_GEO (state, maps) {
      state.maps = maps
    },

    LOAD_DATA (state, csv) {
      const header = csv[0]

      const result = csv
        .splice(1)
        .map(iter => zipObject(header, iter))
        .filter(iter => !!iter.name)
        .map(row => ({
          fid: parseInt(row.kid, 10) === 301 ? 2 : Math.floor(row.kid / 100), // Move Oslo to Akershus
          ...row,
          score: invalidScore(row.score) ? -1 : parseFloat(row.score),
          kid: parseInt(row.kid, 10),
          year: parseInt(row.year, 10),
          pop: parseInt(row.pop, 10),
          name: row.name.trim()
        }))

      const years = result.map(iter => iter.year)
        .filter((value, index, arr) => arr.indexOf(value) === index)

      state.data = result
      state.years = years
    },

    LOAD_MOVES (state, csv) {
      const header = csv[0]

      const result = csv
        .splice(1)
        .map(iter => zipObject(header, iter))
        .map(iter => {
          const strings = ['name', 'comment', 'merged_legacy_ids']

          return Object.keys(iter)
            .reduce((carry, key) => {
              let value = iter[key]
              if (key === 'merged_legacy_ids') {
                value = value.split(',')
                  .map(n => parseInt(n, 10))
                  .filter(n => n > 0)
              }

              return Object.assign({
                [key]: strings.indexOf(key) === -1 ? parseInt(value, 10) : value
              }, carry)
            }, {})
        })

      state.moves = result
      state.countyMoveMap = result
        .filter(move => move.type === 1)
        .reduce((carry, item) => {
          return {
            [item.id]: item,
            ...carry
          }
        }, {})
    }
  },

  actions: {
    loadData ({ commit }) {
      const fetchData = fetch('data_latest.csv')
        .then(response => response.text())
        .then(data => CSV.parse(data))
        .then(csv => commit('LOAD_DATA', csv))

      const fetchMoves = fetch('moves_latest.csv')
        .then(response => response.text())
        .then(data => CSV.parse(data))
        .then(csv => commit('LOAD_MOVES', csv))

      /*
      const fetchMaps = fetch('norge.json')
        .then(response => response.json())
        .then(maps => commit('LOAD_GEO', maps))

      */
      return Promise.all([fetchData, fetchMoves])
    }
  },

  getters: {
    allCounties: () => counties,
    allMaps: state => ({ ...state.maps }),
    allYears: state => ([...state.years]),
    allMovements: state => ([...state.moves]),

    yearsDropdown: state => ([
      ...state.years
        .map(year => ({ label: year, value: year }))
    ]),

    filteredCounties: state => year => {
      const countyFilter = countyFilterer(state.countyMoveMap, year)

      return counties
        .filter(countyFilter)
    },

    countiesDropdown: (state, getters) => (year) => {
      return [{
        label: 'Alle fylker',
        value: 0
      },
      ...getters.filteredCounties(year)
        .map(county => ({ label: county.name, value: county.id }))
      ]
    },

    filteredData: (state) => (key, value, year) => {
      return [...state.data]
        .filter(iter => !value || iter[key] === value)
        .filter(iter => iter.year === year)
        // .filter(iter => iter.score >= 0)
    },

    searchData: (state, getters) => (query, year) => {
      query = query.toLowerCase().trim()

      if (query.length < 2) {
        return []
      }

      const countiesMatches = getters.filteredCounties(year)
        .filter(matchName(query))
        .map(c => ({
          fid: c.id,
          countyName: c.name
        }))

      const rows = [...state.data]
        .filter(matchName(query))
        .filter(iter => iter.year === year)

      const municipalitiesMatches = createRowData(rows)

      return {
        counties: countiesMatches.slice(0, 5),
        municipalities: municipalitiesMatches.slice(0, 5)
      }
    },

    countyById: () => id => counties.find(c => c.id === id),

    countryScoreAverage: (state, getters) => (year) => {
      const scores = getters.filteredData(null, null, year)
        .map(r => parseFloat(r.score))

      return average(scores)
    },

    countyScoreAverage: (state, getters) => (fid, year) => {
      const scores = getters.filteredData('fid', fid, year)
        .map(r => parseFloat(r.score))

      return average(scores)
    },

    municipalityCount: (state, getters) => year => getters
      .filteredData(null, null, year)
      .length,

    municipalityById: (state, getters) => (kid, year) => {
      const [row] = getters.filteredData('kid', kid, year)

      return row
        ? { id: row.kid, county: row.fid, name: row.name }
        : null
    },

    municipalityStatsById: (state, getters) => (kid, year) => {
      const rows = getters.filteredData('kid', kid, year)

      return averagePoints(rows)
    },

    municipalitiesByQuery: (state, getters) => (fid, year) => {
      const countryRows = getters.filteredData(null, null, year)
      const countryGroups = createRowData(countryRows)
      const countryScores = sortBy(countryGroups, iter => iter.score)
        .reverse()

      const rows = getters.filteredData('fid', fid, year)
      const groups = createRowData(rows)

      return sortBy(groups, iter => iter.score)
        .reverse()
        .map((iter, index) => ({
          countryPosition: countryScores.findIndex(sub => sub.kid === iter.kid) + 1,
          countyPosition: index + 1,
          ...iter
        }))
    }
  }
})
